// AB3 Academy — sandbox session storage + interceptor utilities.
// Используется auth.service.ts (header injection в fetchWithAuth) и
// SandboxSessionContext (React provider, см. src/contexts/SandboxSessionContext.tsx).
//
// FINAL handoff §3.4 + §6 + /plan-eng-review decisions:
//   1A — Context API + sessionStorage (tab-scoped, не LocalStorage).
//   3A — header injection всегда, кроме /api/v1/auth/* (auth refresh).
//   FINAL — header теперь HMAC-signed (server-side key). Фронт ТОЛЬКО ХРАНИТ и
//   шлёт. Recovery после refresh — server endpoint GET /practice-header/{mid},
//   НЕ self-rebuild (нет signing key на клиенте).

import type { SandboxSessionContext, SandboxType } from '../types/sandbox.types';

export const SANDBOX_HEADER_NAME = 'X-Sandbox-Session';
const STORAGE_KEY = 'ab3-sandbox-session';

interface StoredSession {
  header: string;
  schemaName: string;
  expiresAt: string; // ISO 8601
  type: SandboxType;
  // Wire-up context: устанавливается при start-practice, используется
  // глобальным listener'ом в SandboxSessionProvider для finish-practice
  // когда юзер кликает «Проверить» в banner'е (Banner живёт в Layout —
  // не имеет доступа к eid/mid из URL).
  enrollmentId?: number;
  moduleId?: number;
  courseId?: number;
}

// ─── sessionStorage I/O (safe to call in tests where SS may be undefined) ───

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStoredSession(): StoredSession | null {
  const ss = getStorage();
  if (!ss) return null;
  const raw = ss.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.header || !parsed.expiresAt || !parsed.schemaName) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredSession(session: StoredSession): void {
  const ss = getStorage();
  if (!ss) return;
  ss.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession(): void {
  const ss = getStorage();
  if (!ss) return;
  ss.removeItem(STORAGE_KEY);
}

// ─── Codec — base64-url-safe JSON (READ-ONLY) ───────────────────────────────
// FINAL: header теперь подписан HMAC. Фронт может только декодировать payload
// для диагностики/UI; encode/sign — server-only (см. GET /practice-header).

/**
 * Прочитать (не валидировать!) payload header'а для отладки или UI-логов.
 * Backend всё равно проверит HMAC на запросе; этот helper — для console.log
 * и Sentry attachments. Возвращает null если header невалидный по структуре.
 */
export function decodeSandboxHeader(raw: string): SandboxSessionContext | null {
  try {
    const json = base64UrlDecode(raw);
    const parsed = JSON.parse(json) as SandboxSessionContext;
    if (!parsed.session_id || !parsed.type || !parsed.expires_at) return null;
    return parsed;
  } catch {
    return null;
  }
}

function base64UrlDecode(s: string): string {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const std = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  return atob(std);
}

// ─── Expiry check ────────────────────────────────────────────────────────────

export function isSandboxExpired(expiresAt: string | Date): boolean {
  const t = typeof expiresAt === 'string' ? Date.parse(expiresAt) : expiresAt.getTime();
  if (Number.isNaN(t)) return true;
  return t <= Date.now();
}

export function remainingMs(expiresAt: string | Date): number {
  const t = typeof expiresAt === 'string' ? Date.parse(expiresAt) : expiresAt.getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, t - Date.now());
}

// ─── Header injection guard ─────────────────────────────────────────────────

/**
 * Можно ли отправлять X-Sandbox-Session на этот URL.
 *
 * Решение /plan-eng-review 3A: header добавляем ВСЕГДА когда sandbox активен,
 * за одним исключением: /api/v1/auth/* (refresh/login/switch-organization) —
 * там header бессмыслен и может ввести middleware в заблуждение при пересоздании
 * сессии. Backend middleware игнорирует header на non-sandbox-aware routes.
 */
export function isSandboxAllowedUrl(url: string): boolean {
  // Поддерживаем absolute и относительные URL.
  try {
    const path = new URL(url, 'http://localhost').pathname;
    if (path.startsWith('/api/v1/auth/')) return false;
    return true;
  } catch {
    // На совсем странных URL — лучше не отправлять.
    return false;
  }
}

// ─── Get-header-for-request helper (используется в fetchWithAuth) ───────────

/**
 * Достать актуальный header для injection, либо null.
 * Очищает storage если session expired.
 */
export function getSandboxHeaderForRequest(url: string): string | null {
  if (!isSandboxAllowedUrl(url)) return null;
  const stored = readStoredSession();
  if (!stored) return null;
  if (isSandboxExpired(stored.expiresAt)) {
    // Тихо чистим — компоненты узнают через CustomEvent при следующем 410,
    // либо через countdown<=0 в CountdownChip.
    clearStoredSession();
    return null;
  }
  return stored.header;
}

// ─── Response interceptor helpers ───────────────────────────────────────────
//
// FINAL handoff §6 универсальный паттерн: 410 / 403 / 400 sandbox-related →
// все ведут к одному действию (clear store + toast + опц. Sentry).
// Три HTTP-кода — три разные причины:
//   410 — TTL истёк (watchdog дропнул схему или прошло >ttl_seconds).
//   403 — HMAC mismatch / cross-user (потенциальный attack signal — Sentry).
//   400 — битый/некодированный base64 header.

export type SandboxRejectionKind = 'expired' | 'forged' | 'malformed';

/**
 * Классифицировать sandbox-связанную ошибку. Возвращает null если ошибка
 * не относится к sandbox (обычный 410/403/400 другого происхождения).
 *
 * detail: уже-распарсенная строка из response.body.detail.
 * status: HTTP status code.
 */
export function classifySandboxRejection(
  status: number,
  detail: unknown,
): SandboxRejectionKind | null {
  if (typeof detail !== 'string') return null;
  const lower = detail.toLowerCase();

  if (status === 410 && lower.includes('sandbox')) return 'expired';

  if (
    status === 403 &&
    (lower.includes('signature') || lower.includes('session does not belong'))
  ) {
    return 'forged';
  }

  if (status === 400 && lower.includes('sandbox')) return 'malformed';

  return null;
}

/**
 * Sentry-friendly: считать ли rejection потенциальным attack signal.
 * Только 'forged' (HMAC mismatch / cross-user) — остальное либо expiry,
 * либо клиентский bug.
 */
export function isPotentialSandboxAttack(kind: SandboxRejectionKind): boolean {
  return kind === 'forged';
}

// ─── CustomEvent names (UI компоненты подписываются на window) ──────────────

export const SANDBOX_EVENT_EXPIRED = 'ab3-sandbox-expired';
export const SANDBOX_EVENT_INVALID = 'ab3-sandbox-invalid';
export const SANDBOX_EVENT_FORGED = 'ab3-sandbox-forged'; // FINAL §6 — 403 signature mismatch
export const SANDBOX_EVENT_UPDATED = 'ab3-sandbox-updated';

type SandboxEventName =
  | typeof SANDBOX_EVENT_EXPIRED
  | typeof SANDBOX_EVENT_INVALID
  | typeof SANDBOX_EVENT_FORGED
  | typeof SANDBOX_EVENT_UPDATED;

export function dispatchSandboxEvent(name: SandboxEventName, detail?: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

// ─── High-level mutations used by SandboxSessionContext ─────────────────────

export function setSession(opts: {
  header: string;
  schemaName: string;
  expiresAt: string;
  type?: SandboxType;
  enrollmentId?: number;
  moduleId?: number;
  courseId?: number;
}): void {
  writeStoredSession({
    header: opts.header,
    schemaName: opts.schemaName,
    expiresAt: opts.expiresAt,
    type: opts.type ?? 'learning',
    enrollmentId: opts.enrollmentId,
    moduleId: opts.moduleId,
    courseId: opts.courseId,
  });
  dispatchSandboxEvent(SANDBOX_EVENT_UPDATED, { schemaName: opts.schemaName });
}

/** Причины очистки sandbox sessions для UI/Sentry routing. */
export type SandboxClearReason = 'finish' | 'expired' | 'forged' | 'malformed' | 'manual';

export function clearSession(reason: SandboxClearReason = 'manual'): void {
  clearStoredSession();
  switch (reason) {
    case 'expired':
      dispatchSandboxEvent(SANDBOX_EVENT_EXPIRED);
      break;
    case 'forged':
      dispatchSandboxEvent(SANDBOX_EVENT_FORGED);
      break;
    case 'malformed':
      dispatchSandboxEvent(SANDBOX_EVENT_INVALID);
      break;
    default:
      dispatchSandboxEvent(SANDBOX_EVENT_UPDATED);
  }
}
